import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { ActionsTableRow } from '@Components/MainTable/MainTable.styled';
import NoResults from '@Components/NoResults/NoResults';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdSpeedDial, { EdSpeedDialAction } from '@Components/UI/Utilities/EdSpeedDial/EdSpeedDial';
import { TableHeader } from '@Features/generics/generics.type';
import { useTypedSelector } from '@Features/store';
import UserProgress from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Users/UserProgress';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { formatDuration } from '@Utils/formatDuration';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LoopIcon from '@mui/icons-material/Loop';
import ReplayIcon from '@mui/icons-material/Replay';
import TodayIcon from '@mui/icons-material/Today';
import { CircularProgress, IconButton, TableCell, Tooltip } from '@mui/material';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import StudentUnitsModal from '../shared/StudentUnitsModal';
import LectureExpiryModal from './LectureExpiryModal';
import {
	apiData,
	errorMessage,
	progressColor,
	StatusChip,
	SubscriptionStatus,
	useSubscriptionGrants,
} from './subscriptions.shared';
import useStudentList from './useStudentList';

export type LectureRow = {
	id: number;
	name: string;
	code: string;
	standalone_course_id: number;
	standalone_course_title: string;
	parent_active: boolean;
	parent_expired: boolean;
	parent_source_bundle_title: string | null;
	started: boolean;
	expired: boolean | null;
	expire_at: Date | null;
	progress: number | null;
	progress_status: string;
	time: number;
};

type CourseRow = {
	id: number;
	title: string;
	source_bundle_id: number | null;
	source_bundle_title: string | null;
	activate_until: Date | null;
	status: SubscriptionStatus;
	progress: number;
	time: number;
	lectures_count: number;
};

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'COURSE / LECTURE', fieldName: 'title', canSort: false, width: '30%' },
	{ displayName: 'EXPIRY DATE', fieldName: 'expire_at', canSort: false },
	{ displayName: 'ACCESS', fieldName: 'access', canSort: false },
	{ displayName: 'STATUS', fieldName: 'status', canSort: false },
	{ displayName: 'PROGRESS', fieldName: 'progress', canSort: false },
	{ displayName: 'LEARNING TIME', fieldName: 'time', canSort: false },
];

type Action = 'reset' | 'sync' | 'complete';
const ACTION_TEXT: Record<Action, [string, string]> = {
	reset: [
		"Reset this student's progress in this lecture? This permanently deletes their unit progress and learning time, test attempts, assignment submissions, webinar bookings and video views.",
		'Lecture progress reset successfully',
	],
	sync: ['Are you sure you want to syncronize this lecture?', 'Lecture progress syncronized successfully'],
	complete: [
		'Mark every unit of this lecture completed for this student? Their test attempts, submissions, webinar bookings and learning time are kept.',
		'Lecture progress completed successfully',
	],
};

const progressStatus = (progress: number) =>
	progress >= 100 ? 'completed' : progress > 0 ? 'in progress' : 'not started';

type Props = { studentId: number };

/**
 * Subscriptions → Progress: the standalone courses the student holds, each a
 * row that expands (one at a time) to its lectures with the per-lecture
 * actions. Lecture enrollments are created on first open, so un-started
 * lectures have nothing to act on; progress actions are refused server-side
 * while the course subscription is banned or expired.
 */
const StandaloneProgressTable: React.FC<Props> = ({ studentId }) => {
	const { displaySnackbar } = useSnackbar();
	const { canProgress, canExtend } = useSubscriptionGrants();
	const { currentEntity: student } = useTypedSelector((state) => state.Students);
	const { rows: courses, meta, load, reload } = useStudentList<CourseRow>(
		studentId,
		'subscriptions/courses',
		[],
		'Unable to load courses'
	);
	const [expanded, setExpanded] = useState<number | null>(null);
	const [lectures, setLectures] = useState<LectureRow[]>([]);
	const [loadingLectures, setLoadingLectures] = useState(false);
	const [pending, setPending] = useState<{ action: Action; lecture: LectureRow }>();
	const [unitsLecture, setUnitsLecture] = useState<LectureRow>();
	const [expiryLecture, setExpiryLecture] = useState<LectureRow>();

	const loadLectures = useCallback(
		async (courseId: number) => {
			setLoadingLectures(true);
			try {
				const response = await api.post(`user/profile/${studentId}/standalone-lectures`, {
					page: 1,
					perPage: 500,
					filters: [{ standalone_course_id: courseId }],
				});
				setLectures(apiData<LectureRow[]>(response) || []);
			} catch (error: any) {
				displaySnackbar('error', errorMessage(error, 'Unable to load lectures'));
			} finally {
				setLoadingLectures(false);
			}
		},
		[studentId, displaySnackbar]
	);

	const toggle = (courseId: number) => {
		if (expanded === courseId) {
			setExpanded(null);
			return;
		}
		setExpanded(courseId);
		setLectures([]);
		loadLectures(courseId);
	};

	/** after an action: the course row (progress %) and its open lectures */
	const refresh = () => {
		reload();
		if (expanded !== null) loadLectures(expanded);
	};

	const runAction = async (_close: boolean, confirm?: boolean) => {
		const current = pending;
		setPending(undefined);
		if (!confirm || !current) return;
		try {
			await api.post(`standalone-courses/lectures/progress/course/${current.action}`, {
				course_id: current.lecture.id,
				user_ids: [studentId],
				standalone_lecture_id: current.lecture.id,
			});
			displaySnackbar('success', ACTION_TEXT[current.action][1]);
			refresh();
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, `Unable to ${current.action} lecture progress`));
		}
	};

	const renderActions = (row: LectureRow) => {
		if (!row.started) return <TableCell />;
		const parentBlocked = row.parent_active === false || row.parent_expired === true;
		const actions: Array<{ key: string; icon: JSX.Element; title: string; onClick: () => void }> = [];
		if (canExtend) {
			actions.push({ key: 'extend', icon: <TodayIcon />, title: 'Extend Lecture', onClick: () => setExpiryLecture(row) });
		}
		if (canProgress && !parentBlocked) {
			actions.push(
				{ key: 'reset', icon: <ReplayIcon />, title: 'Reset', onClick: () => setPending({ action: 'reset', lecture: row }) },
				{ key: 'sync', icon: <LoopIcon />, title: 'Syncronize', onClick: () => setPending({ action: 'sync', lecture: row }) },
				{ key: 'complete', icon: <CheckIcon />, title: 'Complete', onClick: () => setPending({ action: 'complete', lecture: row }) }
			);
		}
		if (!actions.length) {
			return (
				<TableCell>
					{canProgress && parentBlocked ? (
						<Tooltip title="The course subscription is banned or expired — progress actions are unavailable" placement="left">
							<Muted>Unavailable</Muted>
						</Tooltip>
					) : null}
				</TableCell>
			);
		}
		return (
			<ActionsTableRow>
				<SpeedDial ariaLabel="Lecture actions">
					{actions.map((action) => (
						<SpeedDialAction
							key={action.key}
							icon={action.icon}
							tooltipTitle={action.title}
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								action.onClick();
							}}
						/>
					))}
				</SpeedDial>
			</ActionsTableRow>
		);
	};

	const renderLecture = (row: LectureRow) => (
		<MainTableRow key={`l${row.id}`} hover sx={{ background: '#f7fbff' }}>
			<Tooltip title={row.name} placement="top">
				<LectureCell>{row.name}</LectureCell>
			</Tooltip>
			<TableCell>{row.expire_at ? <DateTimeFormatter date={row.expire_at} /> : '-'}</TableCell>
			<TableCell>{row.started ? <StatusChip status={row.expired ? 'expired' : 'active'} /> : '-'}</TableCell>
			<TableCell>
				<StatusText color={progressColor(row.progress_status)}>{row.progress_status}</StatusText>
			</TableCell>
			<TableCell>
				{row.started ? (
					<Tooltip title="Show unit progress" placement="top">
						<Clickable onClick={() => setUnitsLecture(row)}>
							<UserProgress progress={Number(row.progress || 0)} />
						</Clickable>
					</Tooltip>
				) : (
					'-'
				)}
			</TableCell>
			<TableCell>{row.time > 0 ? formatDuration(row.time) : '-'}</TableCell>
			{renderActions(row)}
		</MainTableRow>
	);

	const renderCourse = (course: CourseRow) => {
		const open = expanded === course.id;
		const progress = Number(course.progress || 0);
		return (
			<React.Fragment key={`c${course.id}`}>
				<MainTableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(course.id)}>
					<TableCell>
						<TitleCell>
							<IconButton size="small" aria-label={open ? 'Collapse course' : 'Expand course'}>
								{open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
							</IconButton>
							<div>
								<Title>{course.title}</Title>
								<Note>
									{course.source_bundle_id ? `via bundle: ${course.source_bundle_title ?? course.source_bundle_id}` : 'Standalone course'}
									{' · '}
									{course.lectures_count} lectures
								</Note>
							</div>
						</TitleCell>
					</TableCell>
					<TableCell>{course.activate_until ? <DateTimeFormatter date={course.activate_until} /> : 'No limit'}</TableCell>
					<TableCell>
						<StatusChip status={course.status} />
					</TableCell>
					<TableCell>
						<StatusText color={progressColor(progressStatus(progress))}>{progressStatus(progress)}</StatusText>
					</TableCell>
					<TableCell>
						<UserProgress progress={progress} />
					</TableCell>
					<TableCell>{Number(course.time) > 0 ? formatDuration(Number(course.time)) : '-'}</TableCell>
					<TableCell />
				</MainTableRow>
				{open &&
					(loadingLectures && !lectures.length ? (
						<MainTableRow>
							<TableCell colSpan={TABLE_HEADS.length + 1} align="center">
								<CircularProgress size={22} />
							</TableCell>
						</MainTableRow>
					) : lectures.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={TABLE_HEADS.length + 1} align="center">
								<Muted>No lectures</Muted>
							</TableCell>
						</MainTableRow>
					) : (
						lectures.map(renderLecture)
					))}
			</React.Fragment>
		);
	};

	return (
		<>
			<EdConfirmModal text={pending ? ACTION_TEXT[pending.action][0] : ''} open={Boolean(pending)} handleClose={runAction} />
			{unitsLecture && student && (
				<StudentUnitsModal
					student={student}
					courseId={unitsLecture.id}
					standaloneLecture
					canToggle={canProgress}
					lockedReason={
						unitsLecture.parent_active === false || unitsLecture.parent_expired === true
							? 'The course subscription is banned or expired — unit progress cannot be changed.'
							: undefined
					}
					onClose={(changed) => {
						setUnitsLecture(undefined);
						if (changed) refresh();
					}}
				/>
			)}
			{expiryLecture && (
				<LectureExpiryModal
					lecture={expiryLecture}
					studentId={studentId}
					onClose={(changed) => {
						setExpiryLecture(undefined);
						if (changed) refresh();
					}}
				/>
			)}
			<MainTable
				onPageChangeRequest={load}
				tableFilters={[]}
				tableHeads={TABLE_HEADS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || 0)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				$hasActions
				renderItems={() =>
					courses.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={TABLE_HEADS.length + 1}>
								<NoResults />
							</TableCell>
						</MainTableRow>
					) : (
						courses.map(renderCourse)
					)
				}
			/>
		</>
	);
};

const TitleCell = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	text-align: start;
`;
const Title = styled.p`
	font-weight: 500;
`;
const Note = styled.p`
	font-size: 0.75rem;
	color: #6e7577;
`;
const LectureCell = styled(TableCell)`
	&.MuiTableCell-root {
		padding-inline-start: 3.5rem;
		text-align: start;
	}
`;
const StatusText = styled.div<{ color: string }>`
	color: ${({ color }) => color};
	font-weight: 500;
	text-transform: capitalize;
`;
const Clickable = styled.div`
	cursor: pointer;
`;
const Muted = styled.span`
	color: #a4a4a4;
	font-size: 0.8rem;
`;
const SpeedDial = styled(EdSpeedDial)`
	z-index: 999;
`;
const SpeedDialAction = styled(EdSpeedDialAction)`
	z-index: 999;
`;

export default StandaloneProgressTable;
